import { useState, useEffect } from 'react';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { TrashIcon } from '@heroicons/react/24/outline';
import { StackableModal, DatePicker } from '../components/nextgen';
import { AvatarGenerated } from '../components/Avatar';
import { getUserFullName, classNames } from '../components/helpers/functions';
import useModal from '../hooks/useModal';
import { updateRecurrenceRule, deleteRecurrenceRule } from '../api/tasks';

const MODAL_NAME = 'editRecurrenceRule';

// Recurrence options
const RECURRENCE_OPTIONS = [
	{ value: 'daily', label: 'Daily' },
	{ value: 'weekly', label: 'Weekly' },
	{ value: 'biweekly', label: 'Biweekly' },
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'quarterly', label: 'Quarterly' },
	{ value: 'biyearly', label: 'Bi-yearly' },
	{ value: 'yearly', label: 'Yearly' },
];

// Reminder options (days before due date)
const REMINDER_OPTIONS = [
	{ value: null, label: 'No reminder' },
	{ value: 1, label: '1 day before' },
	{ value: 2, label: '2 days before' },
	{ value: 3, label: '3 days before' },
	{ value: 7, label: '1 week before' },
];

const EditRecurrenceRuleModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openDeleteModal, setClose: closeDeleteModal } =
		useModal('deleteModal');

	const rule = open?.rule;
	const team = open?.team || [];
	const sops = open?.sops || [];
	const onSuccess = open?.onSuccess;
	const onDelete = open?.onDelete;

	// Form state
	const [title, setTitle] = useState('');
	const [content, setContent] = useState('');
	const [assignedTo, setAssignedTo] = useState(null);
	const [recurrence, setRecurrence] = useState('weekly');
	const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
	const [reminderOffset, setReminderOffset] = useState(null);
	const [sopId, setSopId] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState(null);

	// Populate form when modal opens with rule data
	useEffect(() => {
		if (open && rule) {
			setTitle(rule.title || '');
			setContent(rule.content || '');
			setAssignedTo(rule.assignedTo || null);
			setRecurrence(rule.recurrence || 'weekly');
			setRecurrenceEndDate(rule.recurrenceEndDate || '');
			setReminderOffset(rule.reminderOffset || null);
			setSopId(rule.sopId || null);
			setError(null);
		}
	}, [open, rule]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!title.trim()) {
			setError('Task title is required');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			await updateRecurrenceRule(rule.id, {
				title: title.trim(),
				content: content.trim() || null,
				assignedTo: assignedTo || null,
				recurrence,
				recurrenceEndDate: recurrenceEndDate || null,
				reminderOffset: reminderOffset || null,
				sopId: sopId || null,
			});

			if (onSuccess) {
				onSuccess();
			}
			setClose();
		} catch (err) {
			setError(err.message || 'Failed to update recurring task');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = () => {
		openDeleteModal({
			title: 'Delete Recurring Task',
			description: `Are you sure you want to delete "${rule.title || rule.content}"? This will stop all future occurrences. Existing tasks will not be deleted.`,
			confirmText: 'Delete',
			onConfirm: async () => {
				try {
					await deleteRecurrenceRule(rule.id);
					closeDeleteModal();
					if (onDelete) {
						onDelete();
					}
					setClose();
				} catch (err) {
					console.error('Error deleting rule:', err);
				}
			},
		});
	};

	const selectedAssignee = team.find((m) => m.id === assignedTo);
	const selectedRecurrence = RECURRENCE_OPTIONS.find(
		(r) => r.value === recurrence,
	);
	const selectedReminder = REMINDER_OPTIONS.find(
		(r) => r.value === reminderOffset,
	);
	const selectedSop = sops.find((s) => s.id === sopId);

	return (
		<StackableModal open={!!open} onClose={() => setClose()} size="md">
			<form onSubmit={handleSubmit} className="space-y-4">
				<div>
					<h2 className="text-lg font-semibold text-gray-900">
						Edit Recurring Task
					</h2>
					<p className="text-sm text-gray-500 mt-1">
						Update the default settings for this recurring task.
					</p>
				</div>

				{error && (
					<div className="rounded-md bg-red-50 p-3">
						<p className="text-sm text-red-700">{error}</p>
					</div>
				)}

				{/* Task Title */}
				<div>
					<label
						htmlFor="title"
						className="block text-sm font-medium text-gray-700"
					>
						Title <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						id="title"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder="Task title"
						className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
					/>
				</div>

				{/* Task Description */}
				<div>
					<label
						htmlFor="content"
						className="block text-sm font-medium text-gray-700"
					>
						Description
					</label>
					<textarea
						id="content"
						value={content}
						onChange={(e) => setContent(e.target.value)}
						placeholder="Add details about this task..."
						rows={3}
						className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
					/>
				</div>

				{/* Assign To */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Assign to
					</label>
					<Listbox value={assignedTo} onChange={setAssignedTo}>
						<div className="relative">
							<Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
								{selectedAssignee ? (
									<div className="flex items-center gap-2">
										<AvatarGenerated
											id={getUserFullName(selectedAssignee)}
											size="xxs"
										/>
										<span>{getUserFullName(selectedAssignee)}</span>
									</div>
								) : (
									<span className="text-gray-500">Unassigned</span>
								)}
								<ChevronUpDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
							</Listbox.Button>
							<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
								<Listbox.Option
									value={null}
									className={({ active }) =>
										classNames(
											'cursor-pointer select-none relative py-2 pl-3 pr-9',
											active ? 'bg-indigo-50' : '',
										)
									}
								>
									<span className="text-gray-500">Unassigned</span>
								</Listbox.Option>
								{team
									?.filter((m) => m.active && !m.archived)
									?.map((member) => (
										<Listbox.Option
											key={member.id}
											value={member.id}
											className={({ active, selected }) =>
												classNames(
													'cursor-pointer select-none relative py-2 pl-3 pr-9',
													active ? 'bg-indigo-50' : '',
													selected ? 'bg-indigo-100' : '',
												)
											}
										>
											{({ selected }) => (
												<div className="flex items-center gap-2">
													<AvatarGenerated
														id={getUserFullName(member)}
														size="xxs"
													/>
													<span className={selected ? 'font-semibold' : ''}>
														{getUserFullName(member)}
													</span>
													{selected && (
														<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
													)}
												</div>
											)}
										</Listbox.Option>
									))}
							</Listbox.Options>
						</div>
					</Listbox>
				</div>

				{/* Recurrence */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Recurrence
					</label>
					<Listbox value={recurrence} onChange={setRecurrence}>
						<div className="relative">
							<Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
								<span>{selectedRecurrence?.label || 'Weekly'}</span>
								<ChevronUpDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
							</Listbox.Button>
							<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
								{RECURRENCE_OPTIONS.map((option) => (
									<Listbox.Option
										key={option.value}
										value={option.value}
										className={({ active, selected }) =>
											classNames(
												'cursor-pointer select-none relative py-2 pl-3 pr-9',
												active ? 'bg-indigo-50' : '',
												selected ? 'bg-indigo-100' : '',
											)
										}
									>
										{({ selected }) => (
											<div className="flex items-center justify-between">
												<span className={selected ? 'font-semibold' : ''}>
													{option.label}
												</span>
												{selected && (
													<CheckIcon className="h-4 w-4 text-indigo-600" />
												)}
											</div>
										)}
									</Listbox.Option>
								))}
							</Listbox.Options>
						</div>
					</Listbox>

					{/* Recurrence End Date */}
					<div className="mt-2">
						<label className="block text-xs text-gray-500 mb-1">
							End recurrence on (optional)
						</label>
						<DatePicker
							value={recurrenceEndDate}
							onChange={setRecurrenceEndDate}
							placeholder="No end date"
							className="w-full"
						/>
					</div>
				</div>

				{/* Reminder */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Due date reminder
					</label>
					<Listbox value={reminderOffset} onChange={setReminderOffset}>
						<div className="relative">
							<Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
								<span
									className={selectedReminder?.value ? '' : 'text-gray-500'}
								>
									{selectedReminder?.label || 'No reminder'}
								</span>
								<ChevronUpDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
							</Listbox.Button>
							<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
								{REMINDER_OPTIONS.map((option) => (
									<Listbox.Option
										key={option.value || 'none'}
										value={option.value}
										className={({ active, selected }) =>
											classNames(
												'cursor-pointer select-none relative py-2 pl-3 pr-9',
												active ? 'bg-indigo-50' : '',
												selected ? 'bg-indigo-100' : '',
											)
										}
									>
										{({ selected }) => (
											<div className="flex items-center justify-between">
												<span className={selected ? 'font-semibold' : ''}>
													{option.label}
												</span>
												{selected && (
													<CheckIcon className="h-4 w-4 text-indigo-600" />
												)}
											</div>
										)}
									</Listbox.Option>
								))}
							</Listbox.Options>
						</div>
					</Listbox>
				</div>

				{/* SOP - only show if SOPs are available */}
				{sops.length > 0 && (
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Standard Operating Procedure
						</label>
						<Listbox value={sopId} onChange={setSopId}>
							<div className="relative">
								<Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
									<span className={selectedSop ? '' : 'text-gray-500'}>
										{selectedSop?.name || 'No SOP'}
									</span>
									<ChevronUpDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
								</Listbox.Button>
								<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
									<Listbox.Option
										value={null}
										className={({ active }) =>
											classNames(
												'cursor-pointer select-none relative py-2 pl-3 pr-9',
												active ? 'bg-indigo-50' : '',
											)
										}
									>
										<span className="text-gray-500">No SOP</span>
									</Listbox.Option>
									{sops.map((sop) => (
										<Listbox.Option
											key={sop.id}
											value={sop.id}
											className={({ active, selected }) =>
												classNames(
													'cursor-pointer select-none relative py-2 pl-3 pr-9',
													active ? 'bg-indigo-50' : '',
													selected ? 'bg-indigo-100' : '',
												)
											}
										>
											{({ selected }) => (
												<div>
													<div className="flex items-center justify-between">
														<span className={selected ? 'font-semibold' : ''}>
															{sop.name}
														</span>
														{selected && (
															<CheckIcon className="h-4 w-4 text-indigo-600" />
														)}
													</div>
													{sop.description && (
														<p className="text-xs text-gray-500 mt-0.5">
															{sop.description}
														</p>
													)}
												</div>
											)}
										</Listbox.Option>
									))}
								</Listbox.Options>
							</div>
						</Listbox>
					</div>
				)}

				{/* Actions */}
				<div className="flex justify-end pt-4 border-t border-gray-100">
					<div className="flex gap-3">
						<button
							type="button"
							onClick={() => setClose()}
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={isSubmitting || !title.trim()}
							className={classNames(
								'px-4 py-2 text-sm font-medium text-white rounded-md',
								isSubmitting || !title.trim()
									? 'bg-indigo-400 cursor-not-allowed'
									: 'bg-indigo-600 hover:bg-indigo-700',
							)}
						>
							{isSubmitting ? 'Saving...' : 'Save Changes'}
						</button>
					</div>
				</div>
			</form>
		</StackableModal>
	);
};

export default EditRecurrenceRuleModal;
